/**
 * Programmer un stage : un geste, N centres, N semaines.
 *
 * ## La demande, du 9 septembre 2026
 *
 * > « Programmer les Poussins dans 4 centres sur 5, semaines 1 et 2 du congé
 * >   d'automne, avec tarif normal et tarif réduit et le nombre de places, sans
 * >   tarif local, en un seul clic. Dans le 5e centre j'ai un tarif local à
 * >   appliquer, alors je programme un stage Poussins dans ce centre avec mes
 * >   conditions. La généralité se fait en un clic, la minorité en un clic
 * >   aussi. »
 *
 * Ce formulaire est ce geste. Un stage, les semaines cochées, les centres
 * cochés, un jeu de prix, un nombre de places — et huit sessions.
 *
 * ## Ce qu'il remplace, et ce qu'il a remplacé
 *
 * L'ancien écran ouvrait un modal de treize champs par session : 76 modals et
 * ~990 champs pour l'été 2026.
 *
 * Une première version de ce chantier faisait pire à sa manière : elle
 * demandait de déclarer un barème par (centre, stage) AVANT de pouvoir
 * programmer. « Les Poussins à 84 € dans quatre centres » y devenait quatre
 * lignes portant les mêmes chiffres — la même répétition, remontée d'un étage.
 * Le prix appartient au geste, pas à une grille à pré-remplir.
 *
 * ## Le tarif proratisé est montré AVANT le clic
 *
 * Chaque semaine choisie affiche le tarif qu'elle portera. C'est la réponse au
 * défaut le plus coûteux mesuré : la règle « semaine de 4 jours = tarif × 4/5 »
 * existait, et n'était appliquée nulle part — 3 019 € d'écart sur trois
 * périodes. Un tarif qu'on découvre après coup est un tarif qu'on n'a pas
 * choisi.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  History,
  Info,
  Loader2,
  Sparkles,
} from 'lucide-react';
import clsx from 'clsx';
import {
  programmer,
  tarifsPratiques,
  type ResultatDeGeste,
  type Semaine,
  type SessionProgrammee,
  type TarifPratique,
} from '../../../../lib/programme/gateway.supabase';
import {
  apercuParSemaine,
  avis,
  bloque,
  messageDErreur,
  nombreOuNull,
  phraseDeJours,
  portee,
  prix,
  resumeDuGeste,
  type Saisie,
  type SemaineChoisie,
} from '../../../../lib/programme/programmation';
import {
  assemblerHeureDeMur,
  decouperHeureDeMur,
  heuresProposees,
  phraseDOuverture,
  versInstant,
} from '../../../../lib/programme/ouverture';

interface Centre {
  id: string;
  name: string;
}
interface Stage {
  id: string;
  title: string;
}
interface Condition {
  id: string;
  label: string;
}

interface Props {
  periodId: string;
  periodeLabel: string;
  annee: number;
  semaines: Semaine[];
  centres: Centre[];
  stages: Stage[];
  conditions: Condition[];
  /** Ce qui est déjà programmé : pour dire ce que le geste va écraser. */
  dejaProgramme: SessionProgrammee[];
  onProgramme: () => void;
  onErreur: (message: string) => void;
}

const VIDE: Saisie = {
  stage_id: '',
  center_ids: [],
  semaine_ids: [],
  prix_normal: '',
  prix_reduit: '',
  prix_local: '',
  prix_local_reduit: '',
  tarif_condition_id: '',
  capacite: '',
  ouverture: '',
};

function dateCourte(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-BE', {
    day: '2-digit',
    month: '2-digit',
  });
}

const ProgrammerUnStage = ({
  periodId,
  periodeLabel,
  annee,
  semaines,
  centres,
  stages,
  conditions,
  dejaProgramme,
  onProgramme,
  onErreur,
}: Props) => {
  const [saisie, setSaisie] = useState<Saisie>(VIDE);
  const [enCours, setEnCours] = useState(false);
  const [compteRendu, setCompteRendu] = useState<ResultatDeGeste[] | null>(null);
  const [reprises, setReprises] = useState<TarifPratique[]>([]);

  /*
    Les tarifs pratiqués la même période l'année d'avant. Un geste par jeu de
    prix — soit exactement ce qu'il y a à rejouer.
  */
  useEffect(() => {
    let vivant = true;
    if (!periodId) return;
    tarifsPratiques(periodId, annee - 1)
      .then((t) => {
        if (vivant) setReprises(t);
      })
      .catch(() => {
        if (vivant) setReprises([]);
      });
    return () => {
      vivant = false;
    };
  }, [periodId, annee]);

  const semainesChoisies = useMemo<SemaineChoisie[]>(
    () => semaines.map((s) => ({
      id: s.id,
      libelle: s.libelle,
      debut: s.debut,
      fin: s.fin,
      jours_ouverts: s.jours_ouverts,
      jours_reference: s.jours_reference,
    })),
    [semaines],
  );

  const existantes = useMemo(
    () =>
      dejaProgramme.map((s) => ({
        session_id: s.session_id,
        semaine_id: s.semaine_id,
        center_id: s.center_id,
        stage_id: s.stage_id,
        centre: s.centre,
        semaine: s.semaine,
        capacite: s.capacite,
        inscrits: s.inscrits,
        archived: s.archived,
      })),
    [dejaProgramme],
  );

  const liste = useMemo(
    () => avis(saisie, semainesChoisies, existantes),
    [saisie, semainesChoisies, existantes],
  );
  const empeche = bloque(liste);
  const p = useMemo(() => portee(saisie, existantes), [saisie, existantes]);

  const apercu = useMemo(
    () =>
      apercuParSemaine(
        semainesChoisies.filter((s) => saisie.semaine_ids.includes(s.id)),
        nombreOuNull(saisie.prix_normal),
      ),
    [semainesChoisies, saisie.semaine_ids, saisie.prix_normal],
  );

  const basculer = (champ: 'center_ids' | 'semaine_ids', id: string) =>
    setSaisie((s) => ({
      ...s,
      [champ]: s[champ].includes(id) ? s[champ].filter((x) => x !== id) : [...s[champ], id],
    }));

  const tousLesCentres = () =>
    setSaisie((s) => ({
      ...s,
      center_ids: s.center_ids.length === centres.length ? [] : centres.map((c) => c.id),
    }));

  const toutesLesSemaines = () =>
    setSaisie((s) => ({
      ...s,
      semaine_ids: s.semaine_ids.length === semaines.length ? [] : semaines.map((x) => x.id),
    }));

  /** Reprendre un jeu de prix de l'an dernier : remplit tout sauf les semaines. */
  const reprendre = (t: TarifRepris) =>
    setSaisie((s) => ({
      ...s,
      stage_id: t.stage_id,
      center_ids: t.center_ids.filter((id) => centres.some((c) => c.id === id)),
      prix_normal: String(t.prix_normal),
      prix_reduit: t.prix_reduit === null ? '' : String(t.prix_reduit),
      prix_local: t.prix_local === null ? '' : String(t.prix_local),
      prix_local_reduit: t.prix_local_reduit === null ? '' : String(t.prix_local_reduit),
      tarif_condition_id: t.tarif_condition_id ?? '',
      capacite: t.capacite_mediane === null ? s.capacite : String(t.capacite_mediane),
    }));

  const lancer = async () => {
    setEnCours(true);
    try {
      const resultats = await programmer({
        stage_id: saisie.stage_id,
        center_ids: saisie.center_ids,
        semaine_ids: saisie.semaine_ids,
        prix_normal: nombreOuNull(saisie.prix_normal) as number,
        prix_reduit: nombreOuNull(saisie.prix_reduit),
        prix_local: nombreOuNull(saisie.prix_local),
        prix_local_reduit: nombreOuNull(saisie.prix_local_reduit),
        tarif_condition_id: saisie.tarif_condition_id || null,
        capacite: nombreOuNull(saisie.capacite),
        ouverture: saisie.ouverture || null,
      });
      setCompteRendu(resultats);
      onProgramme();
    } catch (err: unknown) {
      onErreur(messageDErreur(err, 'La programmation a échoué.'));
    } finally {
      setEnCours(false);
    }
  };

  /*
    Après un geste réussi, on garde les prix, le stage et les centres, et on
    vide les semaines : le geste suivant est presque toujours « le même stage
    ailleurs » ou « un autre stage aux mêmes semaines ». Tout effacer
    obligerait à retaper ce qui vient d'être décidé.
  */
  const fermerCompteRendu = () => {
    setCompteRendu(null);
    setSaisie((s) => ({ ...s, semaine_ids: [] }));
  };

  const icone = (gravite: string) =>
    gravite === 'bloquant' ? (
      <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
    ) : gravite === 'attention' ? (
      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
    ) : (
      <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
    );

  const champPrix = (
    champ: 'prix_normal' | 'prix_reduit' | 'prix_local' | 'prix_local_reduit',
    libelle: string,
    obligatoire = false,
  ) => (
    <div>
      <label className="form-label">
        {libelle}
        {obligatoire && <span className="text-red-500"> *</span>}
      </label>
      <input
        type="number"
        step="0.01"
        min="0"
        value={saisie[champ]}
        onChange={(e) => setSaisie({ ...saisie, [champ]: e.target.value })}
        className="form-input"
        placeholder={obligatoire ? 'obligatoire' : '—'}
      />
    </div>
  );

  if (semaines.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-primary-600" />
          Programmer un stage
        </h2>
        <div className="text-center py-10 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Déclarez d’abord les semaines de {periodeLabel} {annee}, juste au-dessus.
          <br />
          Un stage se programme sur une semaine : sans semaine, il n’y a rien à faire.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="mb-5">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary-600" />
          Programmer un stage — {periodeLabel} {annee}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Un stage, les semaines et les centres cochés, un jeu de prix : toutes les
          sessions d’un coup. Le cas particulier se fait en un second geste.
        </p>
      </div>

      {/* --- Reprendre l'an dernier --- */}
      {reprises.length > 0 && (
        <div className="mb-5 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
            <History className="h-4 w-4" />
            Ce qui a été pratiqué en {periodeLabel} {annee - 1}
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Un bouton par jeu de prix. Il remplit le stage, les centres, les tarifs et les
            places — il reste à cocher les semaines.
          </p>
          <div className="flex flex-wrap gap-2">
            {reprises.map((t, i) => (
              <button
                key={`${t.stage_id}-${i}`}
                onClick={() => reprendre(t)}
                className="text-left px-3 py-2 bg-white border border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900">{t.stage}</div>
                <div className="text-xs text-gray-600">
                  {prix(t.prix_normal)}
                  {t.prix_reduit !== null && ` / ${prix(t.prix_reduit)}`}
                  {t.prix_local !== null && ` / local ${prix(t.prix_local)}`}
                  {t.tarif_condition && ` · ${t.tarif_condition}`}
                </div>
                <div className="text-xs text-gray-400">
                  {t.centres.length} centre{t.centres.length > 1 ? 's' : ''} ·{' '}
                  {t.nb_sessions} session{t.nb_sessions > 1 ? 's' : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-5">
        {/* --- Quel stage --- */}
        <div>
          <label className="form-label">
            Stage <span className="text-red-500">*</span>
          </label>
          <select
            value={saisie.stage_id}
            onChange={(e) => setSaisie({ ...saisie, stage_id: e.target.value })}
            className="form-input max-w-md"
          >
            <option value="">Choisir un stage…</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </div>

        {/* --- Quelles semaines --- */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="form-label mb-0">
              Semaines <span className="text-red-500">*</span>
            </label>
            <button onClick={toutesLesSemaines} className="text-sm text-primary-600 hover:underline">
              {saisie.semaine_ids.length === semaines.length ? 'Aucune' : 'Toutes'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {semaines.map((s) => {
              const choisie = saisie.semaine_ids.includes(s.id);
              const ecourtee = s.jours_ouverts < s.jours_reference;
              return (
                <button
                  key={s.id}
                  onClick={() => basculer('semaine_ids', s.id)}
                  className={clsx(
                    'px-3 py-2 rounded-lg border text-sm transition-colors',
                    choisie
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400',
                  )}
                >
                  <span className="font-medium">{s.libelle}</span>
                  <span className={clsx('ml-2', choisie ? 'text-primary-100' : 'text-gray-500')}>
                    {dateCourte(s.debut)}
                  </span>
                  {ecourtee && (
                    <span
                      className={clsx(
                        'ml-2 text-xs',
                        choisie ? 'text-amber-200' : 'text-amber-700',
                      )}
                    >
                      {phraseDeJours(s.jours_ouverts, s.jours_reference)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* --- Quels centres --- */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="form-label mb-0">
              Centres <span className="text-red-500">*</span>
            </label>
            <button onClick={tousLesCentres} className="text-sm text-primary-600 hover:underline">
              {saisie.center_ids.length === centres.length ? 'Aucun' : 'Tous'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {centres.map((c) => {
              const choisi = saisie.center_ids.includes(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => basculer('center_ids', c.id)}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors',
                    choisi
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400',
                  )}
                >
                  {choisi && <Check className="h-3.5 w-3.5" />}
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* --- Les tarifs --- */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-1">
            Tarifs d’une semaine <strong>pleine</strong>
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Une semaine écourtée par un férié voit son tarif normal proratisé. Le tarif
            réduit et le tarif local ne changent pas.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {champPrix('prix_normal', 'Normal', true)}
            {champPrix('prix_reduit', 'Réduit')}
            {champPrix('prix_local', 'Local')}
            {champPrix('prix_local_reduit', 'Local réduit')}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="form-label">Condition tarifaire (campagne locale)</label>
              <select
                value={saisie.tarif_condition_id}
                onChange={(e) => setSaisie({ ...saisie, tarif_condition_id: e.target.value })}
                className="form-input"
              >
                <option value="">Aucune</option>
                {conditions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">
                Places par session
                {p.aCreer > 0 && <span className="text-red-500"> *</span>}
              </label>
              <input
                type="number"
                min="1"
                value={saisie.capacite}
                onChange={(e) => setSaisie({ ...saisie, capacite: e.target.value })}
                className="form-input"
                placeholder={p.aCreer > 0 ? 'obligatoire' : 'inchangé'}
              />
            </div>
          </div>
        </div>

        {/* --- L'aperçu : le tarif de chaque semaine, avant le clic --- */}
        {apercu.length > 0 && nombreOuNull(saisie.prix_normal) !== null && (
          <div className="flex flex-wrap gap-2">
            {apercu.map((a) => (
              <div
                key={a.semaine_id}
                className={clsx(
                  'px-3 py-1.5 rounded-lg border text-sm',
                  a.proratise
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : 'bg-white border-gray-200 text-gray-700',
                )}
              >
                <span className="font-medium">{a.libelle}</span>
                <span className="text-gray-500"> · {a.jours} · </span>
                <span className="font-medium">{prix(a.prixNormal)}</span>
                {a.proratise && (
                  <span className="text-gray-400 line-through ml-1">
                    {prix(nombreOuNull(saisie.prix_normal))}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* --- L'ouverture --- */}
        <div>
          <label className="form-label">
            Ouverture des inscriptions
            <span className="text-gray-400 font-normal"> — heure de Bruxelles, facultatif</span>
          </label>
          {/*
            Un jour et une heure au lieu d'un `datetime-local`. Le champ libre a
            produit quatre ouvertures différentes sur les 45 sessions de
            l'automne 2026, dont une à 21:17 : la minute se tape à la main, donc
            elle se rate. Une liste fermée supprime la classe d'erreur.
          */}
          <div className="grid grid-cols-2 gap-3 max-w-xs">
            <input
              type="date"
              value={decouperHeureDeMur(saisie.ouverture).jour}
              onChange={(e) =>
                setSaisie({
                  ...saisie,
                  ouverture: assemblerHeureDeMur(
                    e.target.value,
                    decouperHeureDeMur(saisie.ouverture).heure || '09:00',
                  ),
                })
              }
              className="form-input"
            />
            <select
              value={decouperHeureDeMur(saisie.ouverture).heure}
              onChange={(e) =>
                setSaisie({
                  ...saisie,
                  ouverture: assemblerHeureDeMur(
                    decouperHeureDeMur(saisie.ouverture).jour,
                    e.target.value,
                  ),
                })
              }
              className="form-input"
              disabled={!decouperHeureDeMur(saisie.ouverture).jour}
            >
              {heuresProposees(decouperHeureDeMur(saisie.ouverture).heure).map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {saisie.ouverture
              ? phraseDOuverture(versInstant(saisie.ouverture))
              : 'Laissée vide : les ouvertures déjà fixées ne sont pas touchées, et les nouvelles sessions sont visibles dès leur création.'}
          </p>
        </div>

        {/* --- Ce que le geste va faire --- */}
        {liste.length > 0 && (
          <div className="space-y-2">
            {liste.map((a) => (
              <div
                key={a.genre}
                className={clsx(
                  'flex items-start gap-2 p-3 rounded-lg border text-sm',
                  a.gravite === 'bloquant'
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : a.gravite === 'attention'
                      ? 'bg-amber-50 border-amber-200 text-amber-900'
                      : 'bg-blue-50 border-blue-200 text-blue-900',
                )}
              >
                {icone(a.gravite)}
                <span>{a.phrase}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-gray-600">
            {p.total === 0 ? (
              'Cochez au moins une semaine et un centre.'
            ) : (
              <>
                <strong>{p.total}</strong> session{p.total > 1 ? 's' : ''} —{' '}
                {p.aCreer > 0 && `${p.aCreer} à créer`}
                {p.aCreer > 0 && p.aMettreAJour > 0 && ', '}
                {p.aMettreAJour > 0 && `${p.aMettreAJour} à mettre à jour`}
              </>
            )}
          </p>
          <button
            onClick={lancer}
            className="btn-primary flex items-center"
            disabled={enCours || empeche || p.total === 0}
          >
            {enCours ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-5 w-5 mr-2" />
            )}
            Programmer {p.total > 0 && `${p.total} session${p.total > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>

      {/* --- Le compte rendu --- */}
      {compteRendu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-1">C’est programmé</h3>
            <p className="text-sm text-gray-600 mb-4">{resumeDuGeste(compteRendu)}</p>

            {compteRendu.some((r) => r.message) && (
              <div className="mb-4 space-y-2">
                {compteRendu
                  .filter((r) => r.message)
                  .map((r, i) => (
                    <div
                      key={`${r.session_id}-m-${i}`}
                      className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900"
                    >
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>
                        <strong>
                          {r.semaine} · {r.centre}
                        </strong>{' '}
                        — {r.message}
                      </span>
                    </div>
                  ))}
              </div>
            )}

            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Semaine</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Centre</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Jours</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Tarif</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Places</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">État</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {compteRendu.map((r, i) => (
                    <tr key={`${r.session_id}-l-${i}`}>
                      <td className="px-3 py-1.5">{r.semaine}</td>
                      <td className="px-3 py-1.5 text-gray-600">{r.centre}</td>
                      <td className="px-3 py-1.5 text-right">{r.jours_ouverts}</td>
                      <td className="px-3 py-1.5 text-right">
                        {prix(r.prix_normal)}
                        {r.prix_normal !== r.prix_plein && (
                          <span className="text-gray-400 line-through ml-1">
                            {prix(r.prix_plein)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right">{r.capacite}</td>
                      <td className="px-3 py-1.5">
                        <span
                          className={clsx(
                            'px-1.5 py-0.5 rounded text-xs font-medium',
                            r.action === 'creee'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800',
                          )}
                        >
                          {r.action === 'creee' ? 'créée' : 'mise à jour'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3 mt-4">
              Les factures déjà émises ne changent pas : le montant d’une inscription est
              figé à la commande. Seules les commandes à venir prendront ces tarifs.
            </p>

            <div className="flex justify-end mt-6">
              <button onClick={fermerCompteRendu} className="btn-primary">
                Programmer autre chose
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* Le sous-ensemble d'un tarif pratiqué dont le formulaire a besoin. */
type TarifRepris = Pick<
  TarifPratique,
  | 'stage_id'
  | 'center_ids'
  | 'prix_normal'
  | 'prix_reduit'
  | 'prix_local'
  | 'prix_local_reduit'
  | 'tarif_condition_id'
  | 'capacite_mediane'
>;

export default ProgrammerUnStage;
